import React from 'react';

import { Box } from '@nerdwallet/currency';
import { Tracking } from '@nerdwallet/nw-client-lib';
import lazy, { Hydrate } from '../../../lazy/lazy-universal';
import { Browser } from '../../../../types';
import Breakdown from '../../../breakdown/breakdown';
import { Data } from '../types';

import Schedule from './schedule';

import styles from './ac-breakdown.module.less';

// Lazy load components (Tab Content)
const Amortization = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-amortization' */ '../../../amortization-chart/amortization-chart'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const CompareLoanTypes = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-compare-loan-types' */ '../../../compare-loan-types'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const SECTION_NAMES = {
  AMORTIZATION_SCHEDULE: 'Amortization schedule',
  BREAKDOWN: 'Breakdown',
  COMPARE_LOAN_TYPES: 'Compare loan types',
};

const TABS_NAME = [
  SECTION_NAMES.AMORTIZATION_SCHEDULE,
  SECTION_NAMES.BREAKDOWN,
  SECTION_NAMES.COMPARE_LOAN_TYPES,
];

const trackResultsSection = (
  fieldValue: string,
  fieldName = 'results-section'
): void => {
  // Track which section was opened.
  Tracking.formOpen({
    formName: 'ac-results',
    fieldName,
    fieldValue,
  });
};

const onSectionEnter = (index: number): void =>
  trackResultsSection(TABS_NAME[index]);

interface ACBreakdownProps {
  browser: Browser;
  data?: Data;
}

const ACBreakdown: React.FC<ACBreakdownProps> = ({ data, browser }) => {
  const {
    inputs: { loanTerm },
    outputs: { schedule, halfway, monthlySchedule },
  } = data;

  const onTableViewChanged = (expanded: boolean) => {
    const fieldValue = expanded ? 'Loan Details' : 'Loan Payment';
    trackResultsSection(fieldValue, 'Compare loan types');
  };

  const content = [
    {
      label: SECTION_NAMES.AMORTIZATION_SCHEDULE,
      content: (
        <Amortization
          loanTerm={loanTerm}
          schedule={schedule}
          halfway={halfway}
          hideInsight
        />
      ),
    },
    {
      label: SECTION_NAMES.BREAKDOWN,
      content: <Schedule browser={browser} monthlySchedule={monthlySchedule} />,
    },
    {
      label: SECTION_NAMES.COMPARE_LOAN_TYPES,
      content: <CompareLoanTypes onTableViewChanged={onTableViewChanged} />,
    },
  ];

  return (
    <Box>
      <Breakdown
        className={styles['ac-breakdown']}
        content={content}
        onSectionEnter={onSectionEnter}
      />
    </Box>
  );
};

export default ACBreakdown;
